import { z } from 'zod/v4';
import {
  AUDITS_LIST_DEFAULT_PAGE_SIZE,
  AUDITS_LIST_FILTER_MAX_LENGTH,
  AUDITS_LIST_MAX_PAGE_SIZE,
} from './audit-list.js';

/**
 * Sort columns `GET /api/audit-types` accepts — the legacy
 * `SORT_COLUMNS_ENUM` in `audits-service/routes/schemas/v1/request/audit-types.js`
 * verbatim. Each key maps to a SQL expression in
 * `core/db/audit-types/sorts.ts`; keep the two in sync.
 */
export const AuditTypesListSortColumns = [
  'name',
  'createdAt',
  'updatedAt',
  'id',
  'isealMember',
  'providerName',
  'yearFounded',
  'location',
  'externalLink',
  'conformityAssessment',
  'auditCount',
] as const;

export type AuditTypesListSortColumn =
  (typeof AuditTypesListSortColumns)[number];

const sortColumnAlternatives = AuditTypesListSortColumns.join('|');
const sortParamSchema = z
  .string()
  .regex(
    new RegExp(
      `^((${sortColumnAlternatives}):(ASC|DESC|asc|desc))(,((${sortColumnAlternatives}):(ASC|DESC|asc|desc)))*$`,
    ),
  )
  .optional();

/**
 * Query for `GET /api/audit-types`. Deliberate deltas vs the legacy
 * `GET /audits-service/v1/audit-types` querystring (audits batch 4, see
 * `docs/migrate-audits-to-modules.md` §2.4):
 *
 * - `q` and `entity` are DROPPED — the legacy schema accepted both but the
 *   handler never wired them into SQL, and no frontend caller sends them.
 * - `isDropDown` is DROPPED — its legacy branch returned the whole table
 *   unfiltered and unsorted (a pinned handler bug); full-list consumers now
 *   paginate explicitly with `count`.
 *
 * `strictObject` like the legacy `additionalProperties:false`, so the dropped
 * params (or any other unknown key) 400 instead of being silently ignored.
 */
export const AuditTypesListQuerySchema = z.strictObject({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(AUDITS_LIST_MAX_PAGE_SIZE)
    .default(AUDITS_LIST_DEFAULT_PAGE_SIZE),
  sort: sortParamSchema,
  filter: z.string().max(AUDITS_LIST_FILTER_MAX_LENGTH).optional(),
});

export type AuditTypesListQuery = z.infer<typeof AuditTypesListQuerySchema>;

/**
 * One audit-type row on the wire — the legacy list SELECT verbatim: the 16
 * base columns (with `logoFileUrl` from the `files` join, post-#5287), the
 * `auditCount` correlated subquery, and the 15 `created_by_*`/`updated_by_*`
 * meta columns. The table is global (no tenant scoping, no soft delete);
 * deprecated types are returned unless filtered out.
 *
 * `z.looseObject` — undeclared columns ride along. Deliberate delta vs the
 * legacy wire: the `COUNT(*) OVER()` alias `count` leaked onto every row
 * through the legacy `additionalProperties:true` schema and is now undeclared
 * (dropped by the serializer); no consumer read it.
 *
 * `isDeprecated` is an `integer` column that the legacy response schema
 * coerced to boolean — the transformer keeps the boolean wire shape.
 */
export const AuditTypeRowSchema = z.looseObject({
  id: z.string(),
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  impactAreaId: z.string().nullable().optional(),
  externalLink: z.string().nullable().optional(),
  logoFileId: z.string().nullable().optional(),
  logoFileUrl: z.string().nullable().optional(),
  yearFounded: z.number().nullable().optional(),
  location: z.string().nullable().optional(),
  conformityAssessment: z.string().nullable().optional(),
  typeOfOrganization: z.string().nullable().optional(),
  badgeId: z.string().nullable().optional(),
  isealMember: z.string().nullable().optional(),
  providerName: z.string().nullable().optional(),
  isDeprecated: z.boolean(),
  auditCount: z.number(),
  createdAt: z.string().nullable().optional(),
  createdByImpersonateId: z.string().nullable().optional(),
  createdByUserId: z.string().nullable().optional(),
  createdByCompanyId: z.string().nullable().optional(),
  createdByApiKeyId: z.string().nullable().optional(),
  createdByApiKeyLabel: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  updatedByImpersonateId: z.string().nullable().optional(),
  updatedByImpersonateName: z.string().nullable().optional(),
  updatedByUserId: z.string().nullable().optional(),
  updatedByUserName: z.string().nullable().optional(),
  updatedByCompanyId: z.string().nullable().optional(),
  updatedByCompanyName: z.string().nullable().optional(),
  updatedByApiKeyId: z.string().nullable().optional(),
  updatedByApiKeyLabel: z.string().nullable().optional(),
});

export type AuditTypeRow = z.infer<typeof AuditTypeRowSchema>;

export const AuditTypesListResponseSchema = z.object({
  data: z.array(AuditTypeRowSchema),
  meta: z.object({
    count: z.number(),
    page: z.number(),
    pageSize: z.number(),
    totalCount: z.number(),
  }),
});

export type AuditTypesListResponse = z.infer<
  typeof AuditTypesListResponseSchema
>;

const optionalText = (max: number) => z.string().max(max).optional();

/**
 * `POST /api/admin/audit-types` body — the legacy
 * `POST /audits-service/v1/admin/audit-types` fields, with each length capped
 * at its column's size (the legacy schema allowed 255 everywhere, so anything
 * longer than e.g. `type_of_organization varchar(50)` failed in the INSERT and
 * came back as a 500). `yearFounded` stays a string on the wire, as the form
 * sends it, but must be digits: the column is an `integer`. Empty strings are
 * stored as NULL.
 *
 * Deliberately undeclared (`z.object` drops unknown keys, as the legacy AJV
 * `removeAdditional` did): `logoFileUrl`, which the legacy schema accepted but
 * never stored — the URL is read from the `files` row of `logoFileId`.
 */
export const CreateAuditTypeBodySchema = z.object({
  name: z.string().min(1).max(255),
  description: optionalText(255),
  category: optionalText(100),
  impactAreaId: optionalText(36),
  externalLink: optionalText(255),
  logoFileId: optionalText(36),
  yearFounded: z
    .string()
    .regex(/^\d{0,9}$/, 'yearFounded must be a whole number')
    .optional(),
  location: optionalText(255),
  conformityAssessment: optionalText(255),
  typeOfOrganization: optionalText(50),
  badgeId: optionalText(36),
  isealMember: optionalText(100),
  providerName: optionalText(255),
});

export type CreateAuditTypeBody = z.infer<typeof CreateAuditTypeBodySchema>;

/**
 * `PATCH /api/admin/audit-types/:id/usage` body: `true` deprecates the type
 * (hides it from pickers), `false` brings it back. Same as the legacy
 * `PATCH /v1/admin/audit-types/:id/usage` body.
 */
export const SetAuditTypeUsageBodySchema = z.object({
  deprecate: z.boolean(),
});

export type SetAuditTypeUsageBody = z.infer<typeof SetAuditTypeUsageBodySchema>;

/**
 * `PATCH /api/admin/audit-types/:id` body — any subset of the create fields,
 * with the same per-column caps (the legacy schema allowed 255 everywhere, so
 * an over-long value failed in the UPDATE as a 500). An empty string clears
 * the field; `name`, if sent, must be non-empty. `logoFileUrl`, which the old
 * dashboard sends alongside `logoFileId` but nothing stores, is dropped.
 */
export const PatchAuditTypeBodySchema = CreateAuditTypeBodySchema.partial();

export type PatchAuditTypeBody = z.infer<typeof PatchAuditTypeBodySchema>;
